import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { CopyButton } from '../../../../pages/vulnerabilities/components/VulnerabilityClassification'
import type { TwoFactorEnrollment } from '../../../types/twoFactor'
import { qrImageSrc } from './qr'

/**
 * The enrolment half every flow shares: the QR to scan and, for "can't scan
 * it?", the secret to type.
 *
 * `qr_svg` is a self-contained inline SVG. It is drawn through an `<img>` data
 * URI — never injected as markup — so nothing inside it can run, and it is never
 * logged or stored.
 */
export const QrEnrollment: React.FC<{ enrollment: TwoFactorEnrollment }> = ({ enrollment }) => {
  const intl = useIntl()
  const src = qrImageSrc(enrollment.qr_svg)
  // Never leave a blank box: if the image cannot be drawn the setup key below still completes enrolment.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const showQr = src !== null && failedSrc !== src
  return (
    <div className='d-flex flex-column align-items-center gap-4 mb-4'>
      {/* A QR code is only scannable dark-on-light, whatever the theme: the plate is white on purpose. */}
      {showQr ? (
        <div className='bg-white rounded p-3'>
          <img
            src={src}
            alt={intl.formatMessage({ id: 'TWO_FACTOR.qr.alt' })}
            width={192}
            height={192}
            onError={() => setFailedSrc(src)}
          />
        </div>
      ) : (
        <div className='alert alert-warning mb-0' role='alert'>
          {intl.formatMessage({ id: 'TWO_FACTOR.qr.unavailable' })}
        </div>
      )}
      <div className='d-flex flex-column align-items-center gap-2'>
        <span className='text-muted fs-7'>{intl.formatMessage({ id: 'TWO_FACTOR.qr.cantScan' })}</span>
        <div className='d-flex align-items-center justify-content-center flex-wrap gap-2'>
          <code dir='ltr' className='fs-6 text-break' data-testid='two-factor-secret'>
            {enrollment.secret}
          </code>
          <CopyButton value={enrollment.secret} label={intl.formatMessage({ id: 'TWO_FACTOR.qr.copySecret' })} />
        </div>
      </div>
    </div>
  )
}
